import React from 'react'

import clsx from 'clsx'
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
} from '@material-ui/core'
import {
  Settings as SettingsIcon,
  List as ListIcon,
  Grid as GridIcon,
  PlusCircle as PlusCircleIcon,
  Search as SearchIcon,
} from 'react-feather'

import {
  Container,
  ContentHeader,
  FilterButton,
  Filters,
  LoadingFeedback,
  Page,
  Permitted,
} from 'components'
import {
  ActivityModal,
  ActivitiesTable,
  ColumnManagementModal,
  KanbanBoard,
} from './components'

import KanbanProviders from 'providers/KanbanProviders'

import helpers from 'helpers'

import useKanban from 'hooks/useKanban'
import usePermissions from 'hooks/usePermissions'

import constants from 'constants/index'
import useStyles from './styles'

const ActivitiesPanelContent = () => {
  const {
    HIGH,
    MEDIUM,
    LOW,
    SEVERE,
    CUSTOM_ORDER_FIELDS,
    MODULE_OPTIONS,
    ORIGIN_OPTIONS,
    LINK_STATUS_ACTIVE_VALUE,
    LINK_STATUS_SOURCE_REMOVED_VALUE,
    LINK_STATUS_UNLINKED_VALUE,
    VIEW_MODE_KANBAN,
    VIEW_MODE_LIST,
  } = constants.activities

  const classes = useStyles()

  const {
    activityModalKey,
    activityModalOpen,
    activityToEdit,
    boardId,
    columns,
    columnsLoading,
    departments,
    filter,
    handleCloseActivityModal,
    handleCloseColumnModal,
    handleDeleteCard,
    handleListTabChange,
    handleOpenCreate,
    handleOpenEdit,
    handleRemoveAttachment,
    handleSaveActivity,
    handleSaveColumns,
    handleSearchValue,
    hasSourceRemoved,
    hasUnlinked,
    isBoardLoading,
    isSaving,
    isSearching,
    manageColumnsOpen,
    onlyMine,
    refetchKanban,
    searchInput,
    setOnlyMine,
    setViewMode,
    users,
    viewMode,
  } = useKanban()

  const permissions = usePermissions()
  const canManageColumns = permissions.permitted(
    constants.permissions.KANBAN_TASKS.MANAGE_COLUMNS,
  )
  const canDestroyColumn = permissions.permitted(
    constants.permissions.KANBAN_TASKS.DESTROY_COLUMN,
  )

  const isLoading = isBoardLoading || columnsLoading || isSaving

  return (
    <Page title="Painel de Atividades">
      <LoadingFeedback open={isLoading} />
      <Container maxWidth={false} className={classes.container}>
        <ContentHeader title="Painel de Atividades">
          <Button
            variant="contained"
            color="primary"
            startIcon={<SettingsIcon size={16} />}
            onClick={() => {
              if (canManageColumns) {
                helpers.mixpanel.track('Atividades', 'Colunas gerenciadas', {
                  action: 'atividades_colunas_gerenciadas',
                })
              }
              handleCloseColumnModal(true)
            }}
          >
            {canManageColumns
              ? 'Gerenciar Colunas'
              : 'Gerenciar Datas Finalizadas'}
          </Button>
          {viewMode === VIEW_MODE_LIST && (
            <Permitted tag={constants.permissions.KANBAN_TASKS.MANAGE}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PlusCircleIcon size={20} />}
                onClick={() => {
                  helpers.mixpanel.track('Atividades', 'Task criada', {
                    action: 'atividades_task_criada',
                  })
                  handleOpenCreate()
                }}
              >
                Criar
              </Button>
            </Permitted>
          )}
          <FilterButton setDrawerOpen={filter.setDrawerOpen} />
        </ContentHeader>
        <Box display="flex" alignItems="center" gridGap={8} mb={2}>
          <TextField
            fullWidth
            placeholder="Buscar por nome ou ID"
            value={searchInput}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={20} />
                </InputAdornment>
              ),
              endAdornment: isSearching && (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ),
            }}
            variant="outlined"
            size="small"
            onChange={handleSearchValue}
          />
          <FormControlLabel
            className={classes.noWrapLabel}
            control={
              <Switch
                checked={onlyMine}
                onChange={(e) => setOnlyMine(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label="Exibir apenas minhas atividades"
          />
          <ButtonGroup size="small">
            <Tooltip title="Lista">
              <IconButton
                color={viewMode === VIEW_MODE_LIST ? 'primary' : 'default'}
                onClick={() => {
                  if (viewMode !== VIEW_MODE_LIST) {
                    setViewMode(VIEW_MODE_LIST)
                    handleListTabChange(null)
                  }
                }}
                size="small"
              >
                <ListIcon size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Kanban">
              <IconButton
                color={viewMode === VIEW_MODE_KANBAN ? 'primary' : 'default'}
                onClick={() => setViewMode(VIEW_MODE_KANBAN)}
                size="small"
              >
                <GridIcon size={16} />
              </IconButton>
            </Tooltip>
          </ButtonGroup>
        </Box>

        <div
          className={clsx({ [classes.hidden]: viewMode !== VIEW_MODE_KANBAN })}
        >
          <KanbanBoard />
        </div>
        <div
          className={clsx({ [classes.hidden]: viewMode !== VIEW_MODE_LIST })}
        >
          <ActivitiesTable
            onEditCard={handleOpenEdit}
            onDeleteCard={handleDeleteCard}
          />
        </div>
      </Container>

      <Filters
        filter={filter}
        defaultOrder={constants.orderFilter.CUSTOM_FIELD_DESC}
        customOrderFields={CUSTOM_ORDER_FIELDS}
      >
        <input textfieldinput="true" label="Identificador" name="id" />
        <input textfieldinput="true" label="Nome da Tarefa" name="name" />
        <select
          autocompleteselectinput="true"
          label="Responsável"
          name="responsibleId"
          optionvalues={users}
          loading={isLoading}
        />
        <select textfieldinput="true" label="Prioridade" name="priority">
          <option value=""></option>
          <option value={SEVERE}>Severa</option>
          <option value={HIGH}>Alta</option>
          <option value={MEDIUM}>Média</option>
          <option value={LOW}>Baixa</option>
        </select>
        <select textfieldinput="true" label="Status" name="kanbanColumnId">
          <option value=""></option>
          {columns.map((col) => (
            <option key={col.id} value={col.id}>
              {col.title.length > 30 ? col.title.slice(0, 30) + '…' : col.title}
            </option>
          ))}
        </select>
        <input datepickerinput="true" label="Prazo" name="dueDate" />
        <input
          datepickerinput="true"
          label="Data de Criação"
          name="createdAt"
        />
        <select textfieldinput="true" label="Módulo" name="module">
          {MODULE_OPTIONS.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
        <select textfieldinput="true" label="Origem" name="origin">
          {ORIGIN_OPTIONS.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
        {viewMode === VIEW_MODE_LIST && (hasUnlinked || hasSourceRemoved) && (
          <select
            textfieldinput="true"
            label="Vínculo"
            name="linkStatus"
            InputLabelProps={{ shrink: true }}
          >
            <option value={LINK_STATUS_ACTIVE_VALUE}>Ativas</option>
            {hasUnlinked && (
              <option value={LINK_STATUS_UNLINKED_VALUE}>Desvinculadas</option>
            )}
            {hasSourceRemoved && (
              <option value={LINK_STATUS_SOURCE_REMOVED_VALUE}>
                Origem removida
              </option>
            )}
          </select>
        )}
      </Filters>

      <ColumnManagementModal
        open={manageColumnsOpen}
        setOpen={handleCloseColumnModal}
        columns={columns}
        boardId={boardId}
        onSave={handleSaveColumns}
        canManageColumns={canManageColumns}
        canDestroyColumn={canDestroyColumn}
        onConfigurationSaved={refetchKanban}
      />

      <ActivityModal
        key={activityModalKey}
        open={activityModalOpen}
        setOpen={handleCloseActivityModal}
        activity={activityToEdit}
        columns={columns}
        users={users}
        departments={departments}
        onSave={handleSaveActivity}
        onRemoveAttachment={handleRemoveAttachment}
        onEdit={handleOpenEdit}
        isSaving={isSaving}
      />
    </Page>
  )
}

const ActivitiesPanelMain = () => (
  <KanbanProviders>
    <ActivitiesPanelContent />
  </KanbanProviders>
)

export default ActivitiesPanelMain
